import * as React from "react";
import { ChevronLeft, ChevronRight, Loader2, MessageCircle, Users } from "lucide-react";
import {
  Button,
  cn,
  Input,
  Label,
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
} from "@eduviaos/ui-kit";
import { ResponsiveSheetContent } from "../../components/ResponsiveSheetContent";
import { Reveal, SectionCard, SectionHeader } from "../../components/Section";
import {
  useCreateStudyGroup,
  useJoinStudyGroup,
  useJoinStudyGroupByInvite,
  usePathways,
  useStudyGroups,
} from "../../lib/learning/hooks";
import type { StudyGroup } from "../../lib/learning/types";
import { usePortalLocale } from "../../lib/i18n/usePortalLocale";
import { apiErrorMessage } from "../../lib/apiErrorCopy";
import { StudyGroupChatRoom } from "./StudyGroupChatRoom";

const GROUPS_LIST_COLLAPSED_KEY = "portal-groups-list-collapsed";

function useMinLgViewport(): boolean {
  const [matches, setMatches] = React.useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true,
  );

  React.useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return matches;
}

/** Dedicated Groups tab — split chat on md+ / laptop, full-screen chat on mobile (SC-14g). */
export function StudyGroupsHub({
  pendingInvite = null,
  onInviteConsumed,
}: {
  pendingInvite?: string | null;
  onInviteConsumed?: () => void;
}) {
  const { strings, locale } = usePortalLocale();
  const isLargeScreen = useMinLgViewport();
  const groupsQuery = useStudyGroups();
  const pathwaysQuery = usePathways("active");
  const createMut = useCreateStudyGroup();
  const joinMut = useJoinStudyGroup();
  const joinByInviteMut = useJoinStudyGroupByInvite();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [activeGroup, setActiveGroup] = React.useState<StudyGroup | null>(null);
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [listCollapsed, setListCollapsed] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(GROUPS_LIST_COLLAPSED_KEY) === "1";
  });

  React.useEffect(() => {
    if (!groupsQuery.isError) return;
    setListCollapsed(false);
    window.localStorage.removeItem(GROUPS_LIST_COLLAPSED_KEY);
  }, [groupsQuery.isError]);

  const groups = React.useMemo(() => groupsQuery.data?.items ?? [], [groupsQuery.data?.items]);
  const archivedGroups = React.useMemo(
    () => groupsQuery.data?.archived_items ?? [],
    [groupsQuery.data?.archived_items],
  );
  const openGroups = React.useMemo(() => groups.filter((group) => !group.archived_at), [groups]);
  const activePlans = pathwaysQuery.data?.items ?? [];
  const defaultPathway = activePlans[0] ?? null;
  const resolvedActiveGroup = React.useMemo(() => {
    if (!activeGroup) return null;
    return groups.find((g) => g.id === activeGroup.id) ?? activeGroup;
  }, [activeGroup, groups]);

  function toggleListCollapsed() {
    setListCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem(GROUPS_LIST_COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });
  }

  React.useEffect(() => {
    if (!pendingInvite) return;
    let cancelled = false;
    void joinByInviteMut
      .mutateAsync(pendingInvite)
      .then((joined) => {
        if (cancelled) return;
        setActiveGroup(joined);
        void groupsQuery.refetch();
        onInviteConsumed?.();
      })
      .catch((err) => {
        if (cancelled) return;
        setError(apiErrorMessage(err, locale, strings, strings.inviteJoinFailed));
        onInviteConsumed?.();
      });
    return () => {
      cancelled = true;
    };
  }, [pendingInvite]); // eslint-disable-line react-hooks/exhaustive-deps

  async function createGroup() {
    setError(null);
    if (!defaultPathway) {
      setError(strings.needPlanFirst);
      return;
    }
    if (name.trim().length < 3) {
      setError(strings.groupNameMin);
      return;
    }
    try {
      const created = await createMut.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
        pathway_id: defaultPathway.id,
        subject: defaultPathway.subjects[0] ?? null,
      });
      setCreateOpen(false);
      setName("");
      setDescription("");
      setActiveGroup(created);
      await groupsQuery.refetch();
    } catch (err) {
      setError(apiErrorMessage(err, locale, strings, strings.createGroupFailed));
    }
  }

  async function join(groupId: string) {
    try {
      const joined = await joinMut.mutateAsync(groupId);
      setActiveGroup(joined);
      await groupsQuery.refetch();
    } catch (err) {
      setError(apiErrorMessage(err, locale, strings, strings.joinGroupFailed));
    }
  }

  const listPane = (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      <div className="flex shrink-0 items-center gap-2">
        {!listCollapsed && (
          <Reveal className="min-w-0 flex-1">
            <SectionHeader
              title={strings.groupsTitle}
              action={strings.newGroup}
              onAction={() => setCreateOpen(true)}
              /* On a phone this pane is the whole tab, so its title is the
                 page title; on desktop it labels the sidebar beside the chat. */
              level={isLargeScreen ? 2 : 1}
            />
          </Reveal>
        )}
        {isLargeScreen && (
          <button
            type="button"
            aria-label={listCollapsed ? strings.expandGroupList : strings.collapseGroupList}
            onClick={toggleListCollapsed}
            className="text-muted-foreground hover:text-foreground border-border/60 grid size-9 shrink-0 place-items-center rounded-lg border transition-colors"
          >
            {listCollapsed ? (
              <ChevronRight className="size-4" />
            ) : (
              <ChevronLeft className="size-4" />
            )}
          </button>
        )}
      </div>

      {!listCollapsed && (
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          {groupsQuery.isLoading ? (
            <SectionCard className="flex justify-center py-8">
              <Loader2 className="text-muted-foreground size-5 animate-spin" />
            </SectionCard>
          ) : groupsQuery.isError ? (
            <Reveal index={1}>
              <SectionCard className="space-y-3 py-8 text-center">
                <div role="alert" className="space-y-1">
                  <p className="text-sm font-semibold">{strings.groupsLoadFailedTitle}</p>
                  <p className="text-muted-foreground text-xs">{strings.groupsLoadFailedBody}</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void groupsQuery.refetch()}
                >
                  {strings.homeRetry}
                </Button>
              </SectionCard>
            </Reveal>
          ) : openGroups.length === 0 && archivedGroups.length === 0 ? (
            <Reveal index={1}>
              <SectionCard className="space-y-2 py-8 text-center">
                <Users className="text-muted-foreground mx-auto size-8" />
                <p className="text-sm font-semibold">{strings.noGroupsYet}</p>
                <p className="text-muted-foreground text-xs">{strings.noGroupsHint}</p>
              </SectionCard>
            </Reveal>
          ) : (
            openGroups.map((group, i) => (
              <Reveal key={group.id} index={i + 1}>
                {/* A pointer affordance only. Wrapping the row in a <button>
                    nested the Chat/Join buttons inside another button, which
                    is invalid and made screen readers announce the whole card
                    as one control. The inner buttons carry the semantics. */}
                <div
                  onClick={() => group.is_member && setActiveGroup(group)}
                  className={cn(
                    "w-full text-left transition-colors",
                    group.is_member && "cursor-pointer",
                  )}
                >
                  <SectionCard
                    className={cn(
                      "space-y-2",
                      activeGroup?.id === group.id && "ring-primary/40 ring-2",
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-base font-bold">{group.name}</p>
                        {group.description && (
                          <p className="text-muted-foreground line-clamp-2 text-xs">
                            {group.description}
                          </p>
                        )}
                        <p className="text-muted-foreground mt-1 text-xs tabular-nums">
                          {strings.members(group.member_count, group.max_members)}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        {group.is_member ? (
                          <Button
                            size="sm"
                            variant={activeGroup?.id === group.id ? "default" : "outline"}
                            aria-label={strings.chatWithGroup(group.name)}
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveGroup(group);
                            }}
                          >
                            <MessageCircle className="mr-1 size-3.5" />
                            {strings.chat}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={joinMut.isPending}
                            aria-label={strings.joinGroupNamed(group.name)}
                            onClick={(e) => {
                              e.stopPropagation();
                              void join(group.id);
                            }}
                          >
                            {strings.join}
                          </Button>
                        )}
                      </div>
                    </div>
                  </SectionCard>
                </div>
              </Reveal>
            ))
          )}

          {archivedGroups.length > 0 ? (
            <div className="space-y-3 pt-2">
              <p className="text-muted-foreground px-1 text-xs font-semibold tracking-wide">
                {strings.archivedGroups}
              </p>
              {archivedGroups.map((group) => (
                <SectionCard key={group.id} className="space-y-2">
                  <p className="truncate text-base font-bold">{group.name}</p>
                  <p className="text-muted-foreground text-xs">{strings.archivedReadOnly}</p>
                  <Button size="sm" variant="outline" onClick={() => setActiveGroup(group)}>
                    <MessageCircle className="mr-1 size-3.5" />
                    {strings.chat}
                  </Button>
                </SectionCard>
              ))}
            </div>
          ) : null}

          {error && !createOpen && <p className="text-destructive text-xs">{error}</p>}
        </div>
      )}

      {listCollapsed && isLargeScreen && (
        <div className="flex flex-1 flex-col items-center gap-2 pt-2">
          <button
            type="button"
            aria-label={strings.newGroup}
            onClick={() => setCreateOpen(true)}
            className="text-muted-foreground hover:text-foreground border-border/60 grid size-10 place-items-center rounded-xl border transition-colors"
          >
            <Users className="size-4" />
          </button>
          {groups
            .filter((g) => g.is_member)
            .map((group) => (
              <button
                key={group.id}
                type="button"
                aria-label={group.name}
                title={group.name}
                onClick={() => setActiveGroup(group)}
                className={cn(
                  "grid size-10 place-items-center rounded-xl text-xs font-bold transition-colors",
                  activeGroup?.id === group.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/60 text-muted-foreground hover:text-foreground",
                )}
              >
                {group.name.slice(0, 2).toUpperCase()}
              </button>
            ))}
        </div>
      )}
    </div>
  );

  const ListPane = isLargeScreen ? "aside" : "div";

  return (
    <>
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex h-full min-h-0 flex-1 overflow-hidden md:gap-4">
          {!(isLargeScreen && listCollapsed) && (
            /* Only a sidebar when there is a chat pane beside it. On a phone
               this list is the entire tab, and calling it `complementary`
               left the tab with no primary content in the a11y tree. */
            <ListPane
              className={cn(
                "md:border-border/40 flex min-h-0 shrink-0 flex-col overflow-hidden border-r border-transparent md:pr-3",
                !isLargeScreen && activeGroup && "hidden",
                isLargeScreen ? "w-72 lg:w-80" : "w-full",
              )}
            >
              {listPane}
            </ListPane>
          )}

          {isLargeScreen && (
            <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              {resolvedActiveGroup ? (
                <StudyGroupChatRoom
                  group={resolvedActiveGroup}
                  layout="embedded"
                  listCollapsed={listCollapsed}
                  onToggleList={toggleListCollapsed}
                  onClose={() => setActiveGroup(null)}
                />
              ) : (
                <SectionCard className="text-muted-foreground grid h-full flex-1 place-items-center py-12 text-center text-sm">
                  {strings.selectGroupChat}
                </SectionCard>
              )}
            </main>
          )}
        </div>
      </div>

      {resolvedActiveGroup && !isLargeScreen && (
        <StudyGroupChatRoom
          group={resolvedActiveGroup}
          layout="fullscreen"
          onClose={() => setActiveGroup(null)}
        />
      )}

      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <ResponsiveSheetContent>
          <SheetHeader>
            <SheetTitle>{strings.createGroupTitle}</SheetTitle>
            <SheetDescription>{strings.createGroupBlurb}</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            {!defaultPathway && (
              <p className="text-muted-foreground text-xs">{strings.needPlanFirst}</p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="sg-hub-name">{strings.groupName}</Label>
              <Input id="sg-hub-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sg-hub-desc">{strings.descriptionOptional}</Label>
              <Textarea
                id="sg-hub-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            {error && <p className="text-destructive text-xs">{error}</p>}
            <Button
              className="w-full"
              disabled={createMut.isPending || !defaultPathway}
              onClick={() => void createGroup()}
            >
              {createMut.isPending ? "…" : strings.createGroup}
            </Button>
          </div>
        </ResponsiveSheetContent>
      </Sheet>
    </>
  );
}
